import React from "react";
import { FormSectionElements, FormSubSections, LOVValues } from "../../entities/formTemplateDataType";
import { displayField, getRootComponent, triggerSummaryListFilter, updateFilterSelectionCode, isSummaryLinked } from "../../utilities/ElementUtils";
import Utils from "../../utilities/Utils";
import { colour } from "../../theme";
import XMLParser from "../../utilities/XMLParser";
import axios from "axios";
import { apiUrl } from "../../config";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import './SearchAutocompleteComponent.css';
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";

interface Props {
    field: FormSectionElements;
    editable: boolean;
    title;
    labelControlArray;
    requiredField;
    callbackElement;
    isEng: boolean;

}

interface State {
    suggestions: LOVValues[];
    selectedIndex: number;
    searchString: string;
}

export class SearchAutocompleteComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            suggestions: [],
            selectedIndex: 0,
            searchString: '',
        }
    }

    clearValue = () => {
        const field = this.props.field;
        
        // Clear the value and selection code
        field.Value = null;
        field.SelectionCode = null;
        
        // Clear the search string in state
        this.setState({ searchString: "", suggestions: [], selectedIndex: 0 });
        
        // Validate and run javascript
        FormValidationUtils.runJavascript(field);
        
        // Notify parent component
        this.props.callbackElement(getRootComponent(field));
        
        if (isSummaryLinked(field)) {
            updateFilterSelectionCode(field, null);
            triggerSummaryListFilter(field);
        }
    }

    async componentDidMount() {
        const field = this.props.field;
        window.addEventListener('click', (e) => this.handleClickOutsideSuggestionsList(e));
        if (isSummaryLinked(field)) {
            this.syncInputWithValue();
        }
        if (field.LovAPI) {
            const getUrl = field.LovAPI;
            let response: any;

            if (getRootComponent(field).UseProxy == "Y") {
                let transferPayload = {
                    url: getUrl,
                    httpMethod: 'GET',
                    payload: ""
                }

                response = await axios.post(apiUrl() + "/tponTransfer", transferPayload);
            } else {
                response = await axios.get(getUrl);
            }
            field.ListOfLOVValues = response.data;
            this.reconcilePreBoundValueWithLovOptions(field);
            if (isSummaryLinked(field)) {
                this.syncInputWithValue();
            }
            this.props.callbackElement(getRootComponent(field));
        }
    }

    reconcilePreBoundValueWithLovOptions = (field: FormSectionElements): void => {
        const currentValue = field.Value;
        if (currentValue === undefined || currentValue === null) return;

        const currentValueTrimmed = String(currentValue).trim();
        if (!currentValueTrimmed) return;

        const list = field.ListOfLOVValues ?? [];
        const codeMatch = list.find((opt) => String(opt?.code ?? '').trim() === currentValueTrimmed);
        if (codeMatch) {
            field.SelectionCode = codeMatch.code;
            return;
        }

        const normalizedCurrentValue = currentValueTrimmed.toLowerCase();
        const descMatch = list.find((opt) => {
            const description = String(opt?.description ?? '').trim().toLowerCase();
            const fraDescription = String(opt?.FRADescription ?? '').trim().toLowerCase();
            const richText = String(opt?.richText ?? '').replace(/<[^>]+>/g, ' ').trim().toLowerCase();
            const richTextFra = String(opt?.richTextFRA ?? '').replace(/<[^>]+>/g, ' ').trim().toLowerCase();
            return description === normalizedCurrentValue
                || fraDescription === normalizedCurrentValue
                || richText === normalizedCurrentValue
                || richTextFra === normalizedCurrentValue;
        });

        if (descMatch) {
            field.Value = descMatch.code;
            field.SelectionCode = descMatch.code;
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (isSummaryLinked(this.props.field) && prevProps.field.Value !== this.props.field.Value) {
            this.syncInputWithValue();
        }
    }

    syncInputWithValue() {
        const field = this.props.field;
        if (!isSummaryLinked(field)) {
            return;
        }
        if (!field?.Value) {
            if (this.state.searchString !== '') {
                this.setState({ searchString: '' });
            }
            return;
        }
        const lov = field.ListOfLOVValues?.find(item => item.code == field.Value);
        if (!lov) {
            return;
        }
        const displayText = this.getOptionDisplay(lov);
        if (displayText && this.state.searchString !== displayText) {
            this.setState({ searchString: displayText });
        }
    }

    handleClickOutsideSuggestionsList(event: any) {
        const field = this.props.field;
        const suggestionsList = document.getElementById("suggestion-list-" + field.LoadBindName);
        if (!suggestionsList) {
            return;
        }

        // Check if the clicked element is NOT inside the suggestions list.
        if (!suggestionsList.contains(event.target)) {
            suggestionsList.style.display = 'none';
            if ((field as any).searchOnly) {
                this.setState({ suggestions: [] });
            } else if (isSummaryLinked(field)) {
                this.setState({ suggestions: [] });
                this.syncInputWithValue();
            } else if (this.props.field.keepUnmatchedInput) {  // To allow manually entered input.
                this.setState({ searchString: this.props.field.Value, suggestions: [] });
            } else {
                this.setState({ searchString: '', suggestions: [] });
            }
        } 
    }

    filterSuggestions = (searchStr: string) => {
        const lowerSearchStr = searchStr.toLowerCase();
        if (lowerSearchStr.length < 3) {
            this.setState({ suggestions: [] });
            return;
        }

        // If linked to a summary list with dynamic column selector, rebuild options before filtering
        // This ensures options reflect the currently selected column
        const fieldAny = this.props.field as any;
        if (fieldAny?.filterSummaryListId && fieldAny?.filterColumnSelectorId) {
            this.rebuildAutocompleteOptionsFromSummaryList();
        }

        const lovValues = this.props.field.ListOfLOVValues ?? [];
        const matchStrList = lovValues.filter(p => {
            const description = this.props.isEng ? p.description : p.FRADescription;
            const richText = this.props.isEng ? p.richText : p.richTextFRA;
            const plainRichText = (richText ?? "").toString().replace(/<[^>]+>/g, " ");
            const primaryText = (description ?? "").trim();
            const fallbackText = primaryText.length > 0 ? primaryText : (plainRichText.trim().length > 0 ? plainRichText : (p.code ?? ""));
            return fallbackText.toString().toLowerCase().includes(lowerSearchStr) && this.isOptionVisible(p);
        });
        this.setState({ suggestions: matchStrList });
    }

    rebuildAutocompleteOptionsFromSummaryList = () => {
        const fieldAny = this.props.field as any;
        if (!fieldAny?.filterSummaryListId) return;

        // Find the linked PagedSummaryList and call its rebuild function
        const root = getRootComponent(this.props.field);
        const sections = root?.ListOfFormSections ?? [];

        const findAndRebuild = (elements: any[]): boolean => {
            for (const element of elements ?? []) {
                if (element.Id === fieldAny.filterSummaryListId && element._rebuildAutocompleteOptions) {
                    element._rebuildAutocompleteOptions();
                    return true;
                }
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        if (findAndRebuild(sub?.ListOfFormSectionElements)) return true;
                    }
                }
            }
            return false;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                if (findAndRebuild(subSection.ListOfFormSectionElements)) return;
            }
        }
    }

    handleSearch = () => {
        const fieldAny = this.props.field as any;
        if (!fieldAny.enableSearch || !fieldAny.searchTargetSummaryListId) return;

        // Find the target PagedSummaryList and trigger refresh
        const root = getRootComponent(this.props.field);
        const sections = root?.ListOfFormSections ?? [];

        const findAndRefresh = (elements: any[]): boolean => {
            for (const element of elements ?? []) {
                if (element.Id === fieldAny.searchTargetSummaryListId && element._refreshData) {
                    element._refreshData();
                    return true;
                }
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        if (findAndRefresh(sub?.ListOfFormSectionElements)) return true;
                    }
                }
            }
            return false;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                if (findAndRefresh(subSection.ListOfFormSectionElements)) return;
            }
        }
    }

    getOptionDisplay(lov: LOVValues) {
        const description = this.props.isEng ? lov.description : lov.FRADescription;
        if (description && description.trim().length > 0) {
            return description;
        }
        const richText = this.props.isEng ? lov.richText : lov.richTextFRA;
        if (richText && richText.trim().length > 0) {
            return richText.replace(/<[^>]+>/g, ' ').trim();
        }
        return lov.code ?? '';
    }

    isOptionVisible = (lov: LOVValues): boolean => {
        if (lov.FilterField != null && lov.FilterField.length > 0) {
          let filterValue;
    
          let toDisplay = [];
    
          let filterField = [];
    
          if (Array.isArray(lov.FilterField)) {
            filterField.push(...lov.FilterField)
          } else {
            filterField.push(lov.FilterField)
          }
    
          filterField.forEach((fieldName) => {
    
    
            let foundFilterField = XMLParser.findSubSectionElement(this.props.field, fieldName);
            try {
              if (foundFilterField != null && lov.FilterValue) {
                filterValue = foundFilterField.Value;
    
                let fieldValue = lov.FilterValue[foundFilterField.LoadBindName];
    
                if (fieldValue) {
                  if (fieldValue.length > 0 && fieldValue?.indexOf(filterValue) == -1) {
                    toDisplay.push(false);
                  } else {
                    toDisplay.push(true);
                  }
                }
              }
            } catch (ex) {
              console.log(ex);
            }
          })
    
          return (toDisplay.filter(p => p == false).length == 0);    
        }
    
        return true;
    }

    generateSuggestions = () => {
        let field = this.props.field;
        let htmlString = [];
        this.state.suggestions.forEach((value, index) => {
            if (index < 6) {
                let description = this.props.isEng ? value.description : value.FRADescription;
                let richText = this.props.isEng ? value.richText : value.richTextFRA;
                htmlString.push(
                    <li className={"ontario-search-autocomplete__suggestion-list__list-item" + (this.state.selectedIndex == index ? " ontario-search-autocomplete__suggestion-list--selected" : "")}
                        key={field.LoadBindName + "_" + value.code + "_" + index}
                        role="option"
                        onMouseOver={() => this.setState({ selectedIndex: index })}
                        onClick={() => {
                            field.Value = value.code;
                            field.SelectionCode = value.code;
                            if (field.UpdateField) {
                                let subsec = field.parent as FormSubSections;
                                subsec.ListOfFormSectionElements.forEach(ele => {
                                    if (ele.LoadBindName == field.UpdateField){
                                        ele.Value = value.code;
                                        ele.SelectionCode = value.code;
                                    }
                                })
                            }
                            const newSearch = isSummaryLinked(field) ? this.getOptionDisplay(value) : "";
                            this.setState({ searchString: newSearch, suggestions: [] })
                            updateFilterSelectionCode(field, field.SelectionCode);
                            triggerSummaryListFilter(field);
                            this.props.callbackElement(getRootComponent(field));
                        }}
                        aria-label={description}>
                        {Utils.isNotNullOrEmpty(richText) ? Utils.parseRichTextIntoReact(richText) : (description || value.code)}
                    </li>
                );
            }
        })
        return htmlString;
    }
    
    render() {
        let field = this.props.field;
        const fieldAny = field as any;
        FormValidationUtils.runJavascript(field);
        let hint;
        const HtmlToReactParser = require('html-to-react').Parser;
        let hintHtml = this.props.isEng ? field.Hint : field.HintFR;

        const htmlToReactParser = new HtmlToReactParser();

        if (field.Hint||field.HintFR) {
            hint = htmlToReactParser.parse(hintHtml);
        }
        let controlArray = [];

        let displayfield: boolean = true;
        
        field.Visible = field.Visible ?? true; 

        let requiredField = this.props.requiredField;
        let title = this.props.title;

        displayfield = displayField(field);
        this.props.field.Visible = displayfield;
        
        // Clear value and trigger filter when field becomes invisible
        if (!displayfield) {
            if (isSummaryLinked(field) && !fieldAny._invisibleCleared) {
                fieldAny._invisibleCleared = true;
                // Clear value and trigger filter even if field is already empty
                // This ensures the summary list re-evaluates and excludes this hidden field
                this.clearValue();
            }
            return null;
        }
        
        // Reset the cleared flag when visible again
        if (fieldAny._invisibleCleared) {
            fieldAny._invisibleCleared = false;
        }

        let fieldValueRichText = null;
        if (field.Value) {
            const lovValue = field.ListOfLOVValues.find((lov) => lov.code == field.Value);
            if (lovValue && this.props.isEng) fieldValueRichText = lovValue.richText ?? lovValue.description;
            if (lovValue && !this.props.isEng) fieldValueRichText = lovValue.richTextFRA ?? lovValue.FRADescription;
        }
        
        //update the classname of input field if highlight on error is enabled
       const inputClassName = field.enableHighlightFieldError && !field.Valid ? "ontario-input ontario-search__input ontario-input__error": "ontario-input ontario-search__input";

       const errorBlock = (
                    <div>
                        {field.enableHighlightFieldError && (
                            <LiveValidationErrMsg
                                field={this.props.field}
                                isEng={this.props.isEng}
                                key={"LiveValidationErrMsg_" + field.LoadBindName}
                                id={`${field.LoadBindName}-${field.Id}`}
                            />
                        )}
                        <br />
                    </div>
                );

        controlArray.push(<div className="ontario-search__container">
            {/* //show live validation Error Message if field is invalid */}
            {field.showErrorAboveLabel && errorBlock}

            <label className="ontario-label " htmlFor={field.LoadBindName}>
                {title}
            </label>

             {!field.showErrorAboveLabel && errorBlock}

            {hintHtml ? <p className="ontario-hint" id={`hint-text-${field.LoadBindName}`} tabIndex={field.Valid ? undefined : -1} dangerouslySetInnerHTML={{ __html: hintHtml }}></p> : null}

            <div className="ontario-search__input-suggestion-container">
                <div className="ontario-search__input-container">
                    <input type="search" name="search" autoComplete="off" aria-autocomplete="list" aria-controls={"suggestion-list-" + field.LoadBindName} aria-expanded="false" role="combobox" className={inputClassName}
                        id={field.LoadBindName} 
                        key={'fdIpt_' + field.LoadBindName}
                        value={this.state.searchString}
                        onKeyDown={(e) => {
                            let selectedIndex = this.state.selectedIndex;
                            let searchStr = this.state.searchString;
                            let suggestions = this.state.suggestions;
                            let length = suggestions.length;
                            if (length == 0) return;
                            if (e.key == "ArrowDown") {
                                if (selectedIndex == length-1) selectedIndex = 0;
                                else selectedIndex++;
                            }
                            if (e.key == "ArrowUp") {
                                if (selectedIndex == 0) selectedIndex = length - 1;
                                else selectedIndex--;
                            }
                            if (e.key == "Enter") {
                                let lovVal = suggestions[selectedIndex]
                                field.Value = lovVal.code;
                                field.SelectionCode = lovVal.code;
                                if (field.UpdateField) {
                                    let subsec = field.parent as FormSubSections;
                                    subsec.ListOfFormSectionElements.forEach(ele => {
                                        if (ele.LoadBindName == field.UpdateField){
                                            ele.Value = lovVal.code;
                                            ele.SelectionCode = lovVal.code;
                                        }
                                    })
                                }
                                searchStr = isSummaryLinked(field) ? this.getOptionDisplay(lovVal) : "";
                                suggestions = [];
                                selectedIndex = 0;
                            }
                            this.setState({ selectedIndex: selectedIndex, searchString: searchStr, suggestions: suggestions });
                            if (e.key == "Enter") {
                                updateFilterSelectionCode(field, field.SelectionCode);
                                triggerSummaryListFilter(field);
                                this.props.callbackElement(getRootComponent(field));
                            }

                        }}
                        onChange={(e) => {
                            this.setState({ searchString: e.target.value});
                            this.filterSuggestions(e.target.value);
                            if ((field as any).searchOnly) {
                                field.Value = e.target.value;
                                this.props.callbackElement(getRootComponent(field));
                            }
                            }}/>
                    <input className="ontario-search__reset" id={field.LoadBindName + "-reset-button"} type="reset" aria-label="Clear field"
                        style={{display: this.state.searchString?.length == 0 ? "none" : "block"}}
                        value=""
                        onClick={() => {
                            this.clearValue();
                        }} />
                    <button className="ontario-search__submit" id={field.LoadBindName + "-submit-button"} type="submit" onClick={() => {
                        const fieldAny = field as any;
                        if (fieldAny.enableSearch && fieldAny.searchTargetSummaryListId) {
                            this.handleSearch();
                        } else {
                            this.filterSuggestions(this.state.searchString);
                        }
                    }}>
                        <span className="ontario-show-for-sr">Submit</span>
                        <svg className="ontario-icon" focusable="false"  aria-hidden="true" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                            <use href="#ontario-icon-search"></use>
                        </svg>
                    </button>
                </div>

                <ul className="ontario-search-autocomplete__suggestion-list" 
                    id={"suggestion-list-" + field.LoadBindName} 
                    role="listbox" 
                    aria-describedby={
                        [!field.Valid && `${field.LoadBindName}-${field.Id}-error`, hintHtml && `hint-text-${field.LoadBindName}`]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                    style={{display: this.state.suggestions.length == 0 ? "none" : "block"}}
                    aria-hidden={this.state.suggestions.length == 0 ? "true" : "false"}
                    >
                    {this.generateSuggestions()}
                </ul>

               {this.state.searchString?.length < 3 && fieldValueRichText && 
                    <div className="search-autocomplete-selected-value">
                        <button
                            id={field.LoadBindName + "-reset-button"}
                            type="reset"
                            aria-label="Clear field"
                            className="search-autocomplete-clear-btn"
                            value=""
                            onClick={() => {
                                this.clearValue();
                            }}
                        >
                            <svg
                                className="ontario-icon"
                                focusable="false"
                                viewBox="0 0 24 24"
                                preserveAspectRatio="xMidYMid meet"
                            >
                                <use href="#ontario-icon-close"></use>
                            </svg>
                        </button>

                        <div className="search-autocomplete-richtext">
                            {Utils.parseRichTextIntoReact(fieldValueRichText)}
                        </div>
                    </div>
                }

                <div id="aria-live-region" className="ontario__visually-hidden" aria-live="polite"></div>
            </div>
        </div>);
        return controlArray;
    }
}
